import { z } from 'zod';
import { CitySchema } from './cities';
import { MediaSchema } from './media';
import { PartnerBadgeSchema } from './badge';
import { AgencyTypeFieldsSchema, AgencyTypeSchema, AppTierSchema, ShopifyPartnerTierSchema } from './shared';

export const AddressSchema = z.object({
    address: z.string().nullable().optional(),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
});

export const SocialLinksSchema = z.object({
    /** Listing contact shown on unclaimed profiles; admin-only in expert-manager payloads. */
    contact_email: z.string().nullable().optional(),
    linkedin_url: z.string().nullable().optional(),
    github_url: z.string().nullable().optional(),
    x_url: z.string().nullable().optional(),
    fiverr_url: z.string().nullable().optional(),
    upwork_url: z.string().nullable().optional(),
    toptal_url: z.string().nullable().optional(),
    storetasker_url: z.string().nullable().optional(),
    other_listing_url: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    shopify_directory_link: z.string().nullable().optional(),
    booking_call_url: z.string().nullable().optional(),
    google_merchant: z.string().nullable().optional(),
});

export const ServiceCategorySchema = z.object({
    id: z.number(),
    name: z.string(),
    slug: z.string(),
});

export const ServiceCategoryWithSubcategoriesSchema = z.object({
    id: z.number(),
    name: z.string(),
    slug: z.string(),
    subcategories: z.array(ServiceCategorySchema),
});

export const OfferedServiceSchema = z.object({
    id: z.number().nullable().optional(),
    service_category: ServiceCategorySchema,
    sub_categories: z.array(ServiceCategorySchema),
});

export const ProfileExpertListingSchema = z.object({
    slug: z.string().nullable().optional(),
    bio: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    city: CitySchema.nullable().optional(),
    starting_price: z.number().nullable().optional(),
    timezone: z.string().nullable().optional(),
    phone_no: z.string().nullable().optional(),
    regions_served: z.array(z.string()).optional().nullable(),
    geo_regions: z.array(z.string()).optional().nullable(),
    languages: z.array(z.string()).optional().nullable(),
    subcategory_limit: z.number().int().optional(),
    subcategory_limit_is_override: z.boolean().optional(),
    packaged_services_limit: z.number().int().nullable().optional(),
    faqs_limit: z.number().int().nullable().optional(),
    profile_picture: z.string().nullable().optional(),
    profile_picture_media: MediaSchema.nullable().optional(),
    createdAt: z.coerce.date().nullable().optional(),
    averageResponseTime: z.number().nullable().optional(),
    badges: z.array(PartnerBadgeSchema).optional(),
    partner_tier: ShopifyPartnerTierSchema.nullable().optional(),
    app_tier: AppTierSchema.optional(),
    subcategories: z.array(ServiceCategorySchema.extend({
        category: ServiceCategorySchema,
    })).optional(),
});

export const ProfileAgencyListingSchema = z.object({
    agency_name: z.string().nullable().optional(),
    agency_type: AgencyTypeSchema.optional(),
    agency_type_fields: AgencyTypeFieldsSchema.optional(),
    representative_picture: z.string().nullable().optional(),
    representative_picture_media: MediaSchema.nullable().optional(),
    representative_name: z.string().nullable().optional(),
    representative_role: z.string().nullable().optional(),
});

export const ProfileFreelancerListingSchema = z.object({
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    role: z.string().nullable().optional(),
});

export const OverallReviewsSchema = z.object({
    overall: z.object({
        rating: z.number().min(0).max(5),
        reviews_count: z.number().int().nonnegative(),
    }),
    breakdown: z.object({
        quality_of_work: z.number().min(0).max(5),
        communication: z.number().min(0).max(5),
        respects_timelines: z.number().min(0).max(5),
        value_for_money: z.number().min(0).max(5),
    }),
});

export const ProfileListingSchema = z.object({
    user_type: z.enum(['freelancer', 'agency']),
    expert: ProfileExpertListingSchema.optional(),
    agency: ProfileAgencyListingSchema.optional(),
    freelancer: ProfileFreelancerListingSchema.optional(),
    social_links: SocialLinksSchema.optional(),
    offered_services: z.array(OfferedServiceSchema).optional(),
    reviews: OverallReviewsSchema.optional(),
    address: AddressSchema.optional(),
});

export const PROFILE_STATUS = {
    APPLICATION: 'application',
    DRAFT: 'draft',
    PUBLISHED: 'published',
    UNPUBLISHED: 'unpublished',
} as const;

export const HasExpertProfileSchema = z.object({
    has_expert: z.boolean(),
    is_pro: z.boolean().default(false),
    subcategory_limit: z.number().int().optional(),
    subcategory_limit_is_override: z.boolean().optional(),
    profile_status: z.string().optional().nullable().default(PROFILE_STATUS.DRAFT),
});

// Request schemas for expert listing endpoint
export const ExpertListingProfileRequestSchema = z.object({
    expert_type: z.enum(['freelancer', 'agency']),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    agency_name: z.string().optional(),
    slug: z.string(),
    bio: z.string().nullable().optional(),
    role: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    city_id: z.number().int().nullable().optional(),
    starting_price: z.number().nullable().optional(),
    timezone: z.string().nullable().optional(),
    phone_no: z.string().nullable().optional(),
    regions_served: z.array(z.string()).nullable().optional(),
    geo_regions: z.array(z.string()).nullable().optional(),
    languages: z.array(z.string()).nullable().optional(),
    profile_picture: z.string().nullable().optional(),
    profile_picture_media_id: z.string().nullable().optional(),
    representative_picture: z.string().nullable().optional(),
    representative_picture_media_id: z.string().nullable().optional(),
    representative_name: z.string().nullable().optional(),
    representative_role: z.string().nullable().optional(),
    agency_type: AgencyTypeSchema.nullable().optional(),
    agency_type_fields: AgencyTypeFieldsSchema.optional(),
    address: AddressSchema.nullable().optional(),
    social_links: SocialLinksSchema.nullable().optional(),
    badge_ids: z.array(z.number()).optional(),
    partner_tier: ShopifyPartnerTierSchema.nullable().optional(),
    app_tier: AppTierSchema.optional(),
    subcategories: z.array(z.object({
        id: z.number(),
    })).optional(),
});

export const ExpertListingPackagedServiceRequestSchema = z.object({
    id: z.number().optional(),
    service_name: z.string(),
    slug: z.string().nullable().optional(), // Auto-generated from service_name if not provided
    description: z.string().nullable().optional(),
    starting_price: z.number().min(0),
    price_type: z.enum(['fixed', 'monthly']).optional().default('fixed'),
    featured_media_id: z.string().nullable().optional(),
    gallery_media_ids: z.array(z.string()).nullable().optional(),
});

export const ExpertListingCustomerStoryRequestSchema = z.object({
    id: z.number(),
});

export const ExpertListingFaqRequestSchema = z.object({
    id: z.number().optional(),
    question: z.string(),
    answer: z.string(),
});

export const ExpertListingRequestSchema = z.object({
    profile: ExpertListingProfileRequestSchema.optional(),
    offered_services: z.array(z.object({
        id: z.number().optional(),
        service_category_id: z.number(),
        service_subcategory_ids: z.array(z.number()),
    })).optional(),
    packaged_services: z.array(ExpertListingPackagedServiceRequestSchema).optional(),
    customer_stories: z.array(ExpertListingCustomerStoryRequestSchema).optional(),
    faqs: z.array(ExpertListingFaqRequestSchema).optional(),
});

// TypeScript type exports for request schemas
export type ExpertListingProfileRequest = z.infer<typeof ExpertListingProfileRequestSchema>;
export type ExpertListingPackagedServiceRequest = z.infer<typeof ExpertListingPackagedServiceRequestSchema>;
export type ExpertListingCustomerStoryRequest = z.infer<typeof ExpertListingCustomerStoryRequestSchema>;
export type ExpertListingFaqRequest = z.infer<typeof ExpertListingFaqRequestSchema>;
export type ExpertListingRequest = z.infer<typeof ExpertListingRequestSchema>;
